import {
  JobEntity,
  useSalesTaxTransactionAggregates,
  useSalesTaxTransactions,
  useSourceRecordAggregate,
  useTransactionFilterOptions,
} from '@commenda/api';
import { LoadingIndicator, useToggle } from '@commenda/kakashi';
import mixpanel, { MIXPANEL_EVENTS, MIXPANEL_SURFACE_AREA } from '@commenda/mixpanel';
import { debounce } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { calculateDateRange } from '../common/filters/DateFilter/utils';
import {
  FilterChangeEvent,
  TransactionFilterState,
} from '../common/filters/types';
import { getIntegrationSyncState } from '../common/integrationDataSync/utils';
import {
  calculateOverviewDataForTransactions,
  formatNumberWithCommas,
} from '../utils';

import {
  SortState,
  TransactionListItem,
} from '@commenda/api/new-sales-tax/transactions/types';
import { useNotification } from '../../hooks/useNotification';
import { useSalesTaxRootFiConnectFlow } from '../common/useSalesTaxRootFiConnectFlow';
import DataSyncBanner from '../common/integrationDataSync/DataSyncBanner';
import {
  useCSVJobWatcher,
  useIndirectTaxJobWatcher,
} from '../common/jobs';
import { calculatePaginationRangeForCursorBasedPagination } from '../common/usePaginationRange';

import { SALES_TAX_TABLE_IDS } from '../common/constants';
import { isFilterValueEmpty } from '../common/filters/utils';
import {
  loadFiltersFromSessionStorage,
  loadSearchTermFromSessionStorage,
  loadSortStateFromSessionStorage,
  saveFiltersToSessionStorage,
  saveSearchTermToSessionStorage,
  saveSortStateToSessionStorage,
} from '../common/utils/sessionStorage';
import { useIntegrationSyncAndValidationProgressStatus } from '../data-review/useIntegrationSyncAndValidationProgressStatus';
import { ImportTransactionsChoiceModal } from './modal/ImportTransactionsChoiceModal';
import SalesTaxImportTransactionsModal from './modal/transactions-upload-modal';
import { SalesTaxFiltersType } from './TransactionFilters/config';
import SalesTaxTransactionOverviewSection from './transactions-overview-section';
import TransactionsTableSection from './transactions-table-section';
import TransactionsTabEmptyState from './TransactionsTabEmptyState';
import { FeatureLaunch, isFeatureLaunched } from '@commenda/feature-launches';

type SalesTaxTabId =
  | 'overview'
  | 'transactions'
  | 'products'
  | 'registrations'
  | 'returns';

interface SalesTaxTransactionsTabProps {
  corporationId: number;
  setCurrentTab: (currentTab: SalesTaxTabId) => void;
  initialFilters?: {
    from_date?: string;
    to_date?: string;
    reporting_date_override_from_date?: string;
    reporting_date_override_to_date?: string;
    transaction_source?: string;
    currency?: string[];
  };
  companyId: number;
  /** ISO 3166-1 country code for the corporation's incorporation country */
  incorporationCountry?: string;
  /** Whether to use the EU/UK upload flow (simplified flow without template selection) */
  isEuUploadFlow?: boolean;
}

const SalesTaxTransactionsTab = ({
  corporationId,
  setCurrentTab,
  initialFilters = {},
  companyId,
  incorporationCountry,
  isEuUploadFlow = false,
}: SalesTaxTransactionsTabProps) => {
  const [currentCursor, setCurrentCursor] = useState<string | null>(null);
  const [cursorHistory, setCursorHistory] = useState<string[]>([]);
  const [pageSize] = useState(100);
  const FIRST_PAGE_CURSOR = 'FIRST_PAGE_CURSOR';
  const [hasInitialData, setHasInitialData] = useState(false);
  const [filters, _setFilters] = useState<TransactionFilterState>({
    corporationId,
    ...initialFilters,
  });

  const setFilters = useCallback(
    (
      updater:
        | TransactionFilterState
        | ((prev: TransactionFilterState) => TransactionFilterState)
    ) => {
      _setFilters((prev) => {
        const next =
          typeof updater === 'function'
            ? (
              updater as (p: TransactionFilterState) => TransactionFilterState
            )(prev)
            : updater;
        saveFiltersToSessionStorage(
          corporationId,
          SALES_TAX_TABLE_IDS.TRANSACTIONS,
          next
        );
        return next;
      });
    },
    [corporationId]
  );

  const [sortState, _setSortState] = useState<SortState | null>(null);

  const setSortState = useCallback(
    (updater: SortState | ((prev: SortState) => SortState)) => {
      _setSortState((prev) => {
        const next =
          typeof updater === 'function'
            ? (updater as (p: SortState) => SortState)(prev)
            : updater;
        saveSortStateToSessionStorage(
          corporationId,
          SALES_TAX_TABLE_IDS.TRANSACTIONS,
          next
        );
        return next;
      });
    },
    [corporationId]
  );

  const [transactionsListData, setTransactionsListData] = useState<
    TransactionListItem[]
  >([]);

  const { setNotification } = useNotification();

  const importTransactionsModal = useToggle();
  const importChoiceModalVisibility = useToggle();
  const { isIntegrationSyncRecordDataProcessing } =
    useIntegrationSyncAndValidationProgressStatus(corporationId);

  const {
    isJobInProgress,
    refreshJobs,
    watchEntityWithRefresh,
    getJobsByEntity,
  } = useIndirectTaxJobWatcher({
    corporation_id: corporationId,
    entity: JobEntity.INVOICE,
  });

  // Stage 1: Watch CSV processing jobs in the logical backend (Prisma Job table)
  const {
    isCSVProcessingInProgress,
    refreshJobs: refreshCsvJobs,
    watchWithRefresh: watchCsvWithRefresh,
  } = useCSVJobWatcher({ corporationId });

  const [searchTerm, _setSearchTerm] = useState<string>('');

  const setSearchTerm = useCallback(
    (updater: string | ((prev: string) => string)) => {
      _setSearchTerm((prev) => {
        const next =
          typeof updater === 'function'
            ? (updater as (p: string) => string)(prev)
            : updater;
        saveSearchTermToSessionStorage(
          corporationId,
          SALES_TAX_TABLE_IDS.TRANSACTIONS,
          next
        );
        return next;
      });
    },
    [corporationId]
  );

  const {
    integrations,
    handleConnectIntegrationClick,
    connectRootFiIntegrationModal,
  } = useSalesTaxRootFiConnectFlow({ corporationId, setNotification });

  const {
    data: transactionsData,
    error: transactionsError,
    mutate: mutateTransactions,
    isLoading: isLoadingTransactions,
  } = useSalesTaxTransactions({
    filters: {
      ...filters,
      search_term: searchTerm,
    },
    cursor: currentCursor === FIRST_PAGE_CURSOR ? null : currentCursor,
    limit: pageSize,
    showTaxBreakdown: false,
    sortState,
  });
  const { aggregates, mutate: mutateAggregates } =
    useSalesTaxTransactionAggregates({
      filters: {
        ...filters,
        target_currency: 'USD',
        search_term: searchTerm,
      },
      sortState,
    });

  const { data: sourceRecordAggregate } =
    useSourceRecordAggregate(+corporationId);

  const shouldShowDataReviewBanner =
    sourceRecordAggregate &&
    (sourceRecordAggregate?.validationPending > 0 ||
      sourceRecordAggregate?.validationInProgress > 0 ||
      sourceRecordAggregate?.awaitingReview > 0 ||
      sourceRecordAggregate?.readyForExport > 0 ||
      sourceRecordAggregate?.validationFailed > 0);

  const { data: filterOptions } = useTransactionFilterOptions(corporationId);

  const hasActiveFilters = useMemo(() => {
    return (
      Object.keys(filters || {}).some(
        (key) => filters[key] && key !== 'corporationId'
      ) || searchTerm !== ''
    );
  }, [filters]);

  const loadTransactionFiltersFromSessionStorage = useCallback(() => {
    const savedFilters: TransactionFilterState = loadFiltersFromSessionStorage(
      corporationId,
      SALES_TAX_TABLE_IDS.TRANSACTIONS
    );
    return {
      corporationId,
      ...savedFilters,
    };
  }, [corporationId]);

  const loadTransactionSearchTermFromSessionStorage = useCallback(() => {
    return (
      loadSearchTermFromSessionStorage(
        corporationId,
        SALES_TAX_TABLE_IDS.TRANSACTIONS
      ) || ''
    );
  }, [corporationId]);

  const loadTransactionSortStateFromSessionStorage = useCallback(() => {
    return loadSortStateFromSessionStorage(
      corporationId,
      SALES_TAX_TABLE_IDS.TRANSACTIONS
    );
  }, [corporationId]);

  useEffect(() => {
    setFilters(loadTransactionFiltersFromSessionStorage());
    setSearchTerm(loadTransactionSearchTermFromSessionStorage());
    setSortState(loadTransactionSortStateFromSessionStorage());
  }, [
    loadTransactionFiltersFromSessionStorage,
    loadTransactionSearchTermFromSessionStorage,
    loadTransactionSortStateFromSessionStorage,
  ]);

  useEffect(() => {
    if (transactionsData && !isLoadingTransactions) {
      // Only update if the data has actually changed
      if (
        JSON.stringify(transactionsData.transactions) !==
        JSON.stringify(transactionsListData)
      ) {
        setTransactionsListData(transactionsData.transactions);
      }
    }
  }, [transactionsData, isLoadingTransactions]);

  const overviewData = calculateOverviewDataForTransactions({
    totalSales: aggregates?.totalSales || 0,
    totalTaxCollected: aggregates?.totalTaxCollected || 0,
    totalTransactions: aggregates?.totalTransactions || 0,
  });

  const handleIntegrationClick = () => {
    mixpanel.track(MIXPANEL_EVENTS.CONNECT_INTEGRATION_CLICKED, {
      surface_area: MIXPANEL_SURFACE_AREA.INDIRECT_TAX_APP,
    });
    handleConnectIntegrationClick();
  };

  const handleImportTransactions = () => {
    mixpanel.track(MIXPANEL_EVENTS.IMPORT_TRANSACTION_CLICKED, {
      surface_area: MIXPANEL_SURFACE_AREA.INDIRECT_TAX_APP,
    });
    if (isFeatureLaunched(FeatureLaunch.INDIRECT_TAX_UPLOAD_TRANSACTION_SERVICE_REQUEST))
      importChoiceModalVisibility.show();
    else
      importTransactionsModal.show();
  };

  const resetCursor = () => {
    setCurrentCursor(null);
    setCursorHistory([]);
  };

  useEffect(() => {
    if (transactionsData?.transactions?.length > 0) {
      setHasInitialData(true);
    }
  }, [transactionsData?.transactions]);

  const handleUploadSuccess = async () => {
    resetCursor();
    refreshJobs();
    refreshCsvJobs();

    setTimeout(async () => {
      await Promise.all([mutateTransactions(), mutateAggregates()]);
    }, 1000);
  };

  const debouncedSearch = useCallback(
    debounce((term: string) => {
      resetCursor();
      setSearchTerm(term);
    }, 500),
    []
  );

  const handleSearch = (term: string) => {
    debouncedSearch(term);
  };

  const handleFilterChange = (event: FilterChangeEvent) => {
    resetCursor();

    if (event.filterId === SalesTaxFiltersType.PERIOD) {
      if (Array.isArray(event.value) && event.value.length === 2) {
        setFilters((prev) => ({
          ...prev,
          from_date: event.value[0],
          to_date: event.value[1],
        }));

        return;
      }

      if (typeof event.value === 'string') {
        const { from_date: fromDate, to_date: toDate } = calculateDateRange(
          event.value
        );
        setFilters((prev) => ({
          ...prev,
          from_date: fromDate,
          to_date: toDate,
        }));
      }
    } else if (event.filterId === SalesTaxFiltersType.REPORTING_PERIOD) {
      if (Array.isArray(event.value) && event.value.length === 2) {
        setFilters((prev) => ({
          ...prev,
          reporting_date_override_from_date: event.value[0],
          reporting_date_override_to_date: event.value[1],
        }));

        return;
      }

      if (typeof event.value === 'string') {
        const { from_date: fromDate, to_date: toDate } = calculateDateRange(
          event.value
        );
        setFilters((prev) => ({
          ...prev,
          reporting_date_override_from_date: fromDate,
          reporting_date_override_to_date: toDate,
        }));
      }
    } else if (!isFilterValueEmpty(event.value)) {
      setFilters((prev) => ({ ...prev, [event.filterId]: event.value }));
    } else {
      setFilters((prev) => {
        const newFilters = { ...prev };
        delete newFilters[event.filterId];
        return newFilters;
      });
    }
  };

  const onFilterClear = (filterId: string) => {
    resetCursor();
    if (filterId === 'period') {
      setFilters((prev) => {
        const newFilters = { ...prev };
        delete newFilters.from_date;
        delete newFilters.to_date;
        return newFilters;
      });
    } else if (filterId === SalesTaxFiltersType.REPORTING_PERIOD) {
      setFilters((prev) => {
        const newFilters = { ...prev };
        delete newFilters.reporting_date_override_from_date;
        delete newFilters.reporting_date_override_to_date;
        return newFilters;
      });
    } else {
      setFilters((prev) => {
        const newFilters = { ...prev };
        delete newFilters[filterId];
        return newFilters;
      });
    }
  };

  const onClearAll = () => {
    resetCursor();
    setFilters({ corporationId });
  };

  const handlePageChange = (newCursor: string | null) => {
    if (newCursor === currentCursor) {
      // Going back
      const previousCursor = cursorHistory[cursorHistory.length - 1];
      setCursorHistory((prev) => prev.slice(0, -1));
      setCurrentCursor(previousCursor || null);
    } else {
      // Going forward
      setCursorHistory((prev) => [...prev, currentCursor || FIRST_PAGE_CURSOR]);
      setCurrentCursor(newCursor);
    }
  };

  const handleSortChange = (srtState: SortState) => {
    resetCursor();
    setSortState(srtState);
  };

  const { startIndexOfPage, endIndexOfPage, totalItemsForPage } =
    calculatePaginationRangeForCursorBasedPagination({
      cursorHistoryLength: cursorHistory.length,
      pageSize,
      currentItemsCount: transactionsListData.length,
      totalItems: transactionsData?.totalTransactions || 0,
    });

  const isIntegrationSyncInProgress = getIntegrationSyncState(integrations);

  useEffect(() => {
    let cleanup: (() => void) | undefined;

    // Start watching only if the entity is processing
    if (isJobInProgress(JobEntity.INVOICE)) {
      cleanup = watchEntityWithRefresh(JobEntity.INVOICE, () => {
        mutateTransactions();
        mutateAggregates();
      });
    }

    return () => {
      if (cleanup) {
        cleanup();
      }
    };
  }, [
    isJobInProgress,
    watchEntityWithRefresh,
    mutateTransactions,
    mutateAggregates,
  ]);

  // Stage 1: Watch CSV processing in the logical backend
  useEffect(() => {
    let cleanup: (() => void) | undefined;

    if (isCSVProcessingInProgress()) {
      cleanup = watchCsvWithRefresh(() => {
        mutateTransactions();
        mutateAggregates();
      });
    }

    return () => {
      if (cleanup) {
        cleanup();
      }
    };
  }, [
    isCSVProcessingInProgress,
    watchCsvWithRefresh,
    mutateTransactions,
    mutateAggregates,
  ]);

  if (
    !transactionsData ||
    transactionsError ||
    (isLoadingTransactions && !hasInitialData)
  ) {
    return (
      <main className="flex items-center justify-center min-h-screen">
        <LoadingIndicator />
      </main>
    );
  }

  return (
    <>
      {connectRootFiIntegrationModal}
      <ImportTransactionsChoiceModal
        open={importChoiceModalVisibility.visible}
        onOpenChange={(v) => importChoiceModalVisibility.setValue(v)}
        companyId={companyId}
        corporationId={corporationId}
        setNotification={setNotification}
        onSelectImportMyself={() => {
          importChoiceModalVisibility.setValue(false);
          importTransactionsModal.show();
        }}
      />
      <SalesTaxImportTransactionsModal
        isTransactionsModalShown={importTransactionsModal.visible}
        setIsTransactionsModalShown={(val) => {
          importTransactionsModal.setValue(val);
        }}
        companyId={companyId}
        setNotification={setNotification}
        onSuccess={handleUploadSuccess}
        corporationId={corporationId}
        incorporationCountry={incorporationCountry}
        isEuUploadFlow={isEuUploadFlow}
      />

      {!hasInitialData &&
        !transactionsData?.transactions?.length &&
        !hasActiveFilters &&
        !getJobsByEntity(JobEntity.INVOICE).length &&
        !(
          isJobInProgress(JobEntity.INVOICE) ||
          isCSVProcessingInProgress() ||
          isIntegrationSyncInProgress ||
          isIntegrationSyncRecordDataProcessing()
        ) ? (
        <main className="flex flex-col justify-center items-center h-[640px]">
          <TransactionsTabEmptyState
            onImportTransactions={handleImportTransactions}
            onConnectIntegration={handleIntegrationClick}
          />
        </main>
      ) : (
        <>
          {(isIntegrationSyncInProgress ||
            isJobInProgress(JobEntity.INVOICE) ||
            isCSVProcessingInProgress() ||
            isIntegrationSyncRecordDataProcessing()) && (
              <DataSyncBanner
                corporationId={corporationId}
                entity={JobEntity.INVOICE}
                isSyncInProgress={isIntegrationSyncRecordDataProcessing()}
              />
            )}
          <main className="flex-1 flex flex-col">
            <SalesTaxTransactionOverviewSection
              corporationId={corporationId}
              overviewData={overviewData}
              handleAddTransactions={handleImportTransactions}
              openIntegrationModal={handleIntegrationClick}
              shouldShowFailedTransactions={shouldShowDataReviewBanner}
            />

            {totalItemsForPage > 0 ? (
              <div className="txt-small-regular text-text-quaternary mt-4 mb-4 px-2">
                Showing {startIndexOfPage}-{endIndexOfPage} of{' '}
                {formatNumberWithCommas(totalItemsForPage)}{' '}
                {totalItemsForPage === 1 ? 'transaction' : 'transactions'}
              </div>
            ) : (
              <div className="txt-small-regular text-text-quaternary mt-4 mb-4 px-2">
                Showing 0-0 of 0 transactions
              </div>
            )}
            <TransactionsTableSection
              corporationId={corporationId}
              pageSize={pageSize}
              data={transactionsListData}
              filters={filters}
              totalTransactions={transactionsData?.totalTransactions}
              nextCursor={transactionsData?.nextCursor}
              currentCursor={currentCursor}
              onPageChange={handlePageChange}
              onSuccess={handleUploadSuccess}
              searchTerm={searchTerm}
              handleSearch={handleSearch}
              sortState={sortState}
              onSortChange={handleSortChange}
              setNotification={setNotification}
              showEstimatedTaxBreakdown={false}
              filterOptions={filterOptions}
              onFilterChange={handleFilterChange}
              onFilterClear={onFilterClear}
              onClearAll={onClearAll}
              defaultDateColumn="date"
              storageKeySuffix="transaction-tab"
              incorporationCountry={incorporationCountry}
              isEuUploadFlow={isEuUploadFlow}
            />
          </main>
        </>
      )}
    </>
  );
};

export default SalesTaxTransactionsTab;
